<script setup>
/* global axios */
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

// Shared input for the Kanban move actions. When `mode` is 'funnel' it shows a
// funnel selector plus a stage selector; when 'stage' it only shows the stage
// selector of the conversation's current funnel (any funnel's stages are
// offered since the linked item resolves its own funnel at runtime).
const props = defineProps({
  modelValue: {
    type: [Array, Object],
    default: () => [],
  },
  mode: {
    type: String,
    default: 'funnel',
    validator: value => ['funnel', 'stage'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'input']);

const { t } = useI18n();
const store = useStore();

const funnels = ref([]);

const accountId = computed(() => store.getters.getCurrentAccountId);

const config = computed(() => {
  const value = Array.isArray(props.modelValue)
    ? props.modelValue[0]
    : props.modelValue;
  return value && typeof value === 'object'
    ? value
    : { funnel_id: null, funnel_stage: null };
});

const updateConfig = patch => {
  const next = [{ ...config.value, ...patch }];
  emit('update:modelValue', next);
  emit('input', next);
};

const funnelId = computed({
  get: () => config.value.funnel_id || null,
  set: value => updateConfig({ funnel_id: value, funnel_stage: null }),
});

const funnelStage = computed({
  get: () => config.value.funnel_stage || null,
  set: value => updateConfig({ funnel_stage: value }),
});

const stageOptions = computed(() => {
  const allStages = [];
  funnels.value.forEach(funnel => {
    if (props.mode === 'funnel' && funnel.id !== funnelId.value) return;
    const stages = funnel.stages || {};
    Object.keys(stages).forEach(key => {
      allStages.push({ value: key, label: key });
    });
  });
  return allStages;
});

onMounted(async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/funnels`
    );
    funnels.value = Array.isArray(data) ? data : data?.payload || [];
  } catch (error) {
    funnels.value = [];
  }
});
</script>

<template>
  <div class="flex flex-col gap-3 flex-1">
    <label v-if="mode === 'funnel'" class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AUTOMATION.KANBAN_MOVE.FUNNEL_LABEL') }}
      </span>
      <select
        v-model="funnelId"
        class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
      >
        <option :value="null" disabled>
          {{ t('AUTOMATION.KANBAN_MOVE.FUNNEL_PLACEHOLDER') }}
        </option>
        <option v-for="funnel in funnels" :key="funnel.id" :value="funnel.id">
          {{ funnel.name }}
        </option>
      </select>
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AUTOMATION.KANBAN_MOVE.STAGE_LABEL') }}
      </span>
      <select
        v-model="funnelStage"
        class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
      >
        <option :value="null" disabled>
          {{ t('AUTOMATION.KANBAN_MOVE.STAGE_PLACEHOLDER') }}
        </option>
        <option
          v-for="option in stageOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </label>
  </div>
</template>
